'use client';
import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import Marquee from 'react-fast-marquee';
import { useLocale } from 'use-intl';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './AiForGoodSection.module.css';
import {
  buildAiForGoodMarqueeRows,
  getAiForGoodCardText,
  getMobileAiForGoodMarqueeRows,
  getAiForGoodLogo,
  hasAiForGoodWechatLink,
  isWechatBrowser,
  isValidAiForGoodLink,
  resolveAiForGoodLink,
  splitAiForGoodRows,
} from './AiForGoodSection.helpers';
import { fetchConferenceAiForGoodByYear } from '../api';
import type {
  ConferenceAiForGoodItem,
  ConferenceAiForGoodListResp,
  ConferenceSite,
} from '../types';

interface AiForGoodSectionProps {
  data?: ConferenceAiForGoodListResp;
  title?: string;
  years?: number[];
  site?: ConferenceSite;
}

const DESKTOP_ROW_OFFSETS = ['-106px', '-289px'];

const renderAiForGoodLogo = (item: ConferenceAiForGoodItem, title: string) => {
  const logo = getAiForGoodLogo(item);
  if (logo) {
    return (
      <Image
        src={logo}
        alt={title}
        className={styles.logoImage}
        loading="lazy"
      />
    );
  }
  return <div className={styles.logoFallback}>{title.slice(0, 1) || 'A'}</div>;
};

const AiForGoodCard = ({
  item,
  locale,
  isMobile = false,
  isLeadDesktopCard = false,
  animationDelay = 0,
  site = 'zh',
}: {
  item: ConferenceAiForGoodItem;
  locale: 'zh' | 'en';
  isMobile?: boolean;
  isLeadDesktopCard?: boolean;
  animationDelay?: number;
  site?: ConferenceSite;
}) => {
  const title = getAiForGoodCardText(item, 'productName', locale);
  const desc = getAiForGoodCardText(item, 'productDesc', locale);
  const detailText = locale === 'zh' ? '查看详情' : 'View Details';
  const isWechat =
    typeof navigator !== 'undefined' && isWechatBrowser(navigator.userAgent);
  const link = resolveAiForGoodLink(item, { isWechat, isMobile });
  const isClickable =
    (isWechat && hasAiForGoodWechatLink(item)) ||
    isValidAiForGoodLink(link);
  const cardTitleClassName = [
    styles.cardTitle,
    site === 'en' && !isMobile ? styles.cardTitleOversea : '',
    site === 'en' && isMobile ? styles.mobileCardTitleOversea : '',
  ].join(' ');
  const cardDescClassName = [
    styles.cardDesc,
    site === 'en' && !isMobile ? styles.cardDescOversea : '',
  ].join(' ');
  const cardClassName = [
    styles.card,
    isMobile ? styles.mobileCard : styles.desktopCard,
    site === 'en' && !isMobile ? styles.desktopCardOversea : '',
    site === 'en' && isMobile ? styles.mobileCardOversea : '',
    !isMobile && isLeadDesktopCard ? styles.desktopCardLead : '',
    isClickable ? styles.cardInteractive : styles.cardDisabled,
  ].join(' ');
  const style = {
    animationDelay: `${animationDelay}ms`,
  } as React.CSSProperties;
  const content = (
    <div className={styles.cardBody}>
      <div className={styles.logoWrap}>{renderAiForGoodLogo(item, title)}</div>
      {isMobile ? (
        <div className={styles.content}>
          <h3 className={cardTitleClassName} title={title || '-'}>
            {title || '-'}
          </h3>
          <p className={cardDescClassName} title={desc || '-'}>
            {desc || '-'}
          </p>
        </div>
      ) : (
        <div className={styles.desktopContentColumn}>
          <div className={styles.content}>
            <h3 className={cardTitleClassName} title={title || '-'}>
              {title || '-'}
            </h3>
            <p className={cardDescClassName} title={desc || '-'}>
              {desc || '-'}
            </p>
          </div>
          {isClickable ? (
            <span className={styles.detailButton} aria-hidden="true">
              <span className={styles.detailButtonText}>{detailText}</span>
              <svg
                className={styles.detailButtonIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="9"
                viewBox="0 0 19 9"
                fill="none"
              >
                <path d="M0 4.11304L18 4.11304" stroke="#0040DD" />
                <path d="M18 4.39038L13 0.390381" stroke="#0040DD" />
                <path d="M18 4.03101L13 8.03101" stroke="#0040DD" />
              </svg>
            </span>
          ) : null}
        </div>
      )}
    </div>
  );

  if (isClickable) {
    return (
      <a
        className={cardClassName}
        href={link}
        rel="noreferrer"
        style={style}
        target="_blank"
      >
        {content}
      </a>
    );
  }

  return (
    <div className={cardClassName} style={style}>
      {content}
    </div>
  );
};

/**
 * AI 向善组件
 */
export default function AiForGoodSection({
  data,
  title,
  years,
  site,
}: AiForGoodSectionProps) {
  const locale = useLocale() === 'zh' ? 'zh' : 'en';
  const [isPending, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  const [sectionData, setSectionData] = useState<ConferenceAiForGoodListResp>(
    data || { list: [], total: 0 }
  );
  const [selectedYear, setSelectedYear] = useState(() => years?.[0] || data?.conferenceYear || new Date().getFullYear());

  useEffect(() => {
    setSectionData(data || { list: [], total: 0 });
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
  }, [data, years]);

  const list = useMemo(() => sectionData?.list || [], [sectionData]);
  const [sourceFirstRow, sourceSecondRow] = useMemo(
    () => splitAiForGoodRows(list),
    [list]
  );
  const [desktopFirstRow, desktopSecondRow] = useMemo(
    () => buildAiForGoodMarqueeRows(list),
    [list]
  );
  const mobileRows = useMemo(() => {
    const [firstRow, secondRow] = getMobileAiForGoodMarqueeRows(list);
    return [firstRow, secondRow].filter((row) => row.length);
  }, [list]);

  const yearOptions = useMemo(() => years || [], [years]);

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    setSelectedYear(year);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceAiForGoodByYear({
        conferenceYear: year,
        site,
      }).then((nextData) => {
        if (requestIdRef.current === requestId) {
          setSectionData(nextData);
        }
      });
    });
  };

  return (
    <section
      id="ai-for-good"
      className={`${styles.section} ${site === 'en' ? styles.enSection : ''}`.trim()}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <SectionTitle
            className={`mb-0 mobile:text-[1.5rem] ${styles.title} ${locale === 'en' ? styles.englishTitle : ''}`}
          >
            {title}
          </SectionTitle>
          <SectionYearSelector
            selectedYear={selectedYear}
            years={yearOptions}
            onChange={handleYearChange}
          />
        </div>
      </div>

      {list.length ? (
        <div className={styles.desktopRows}>
          {[desktopFirstRow, desktopSecondRow].map((row, rowIndex) =>
            row.length ? (
              <div className={styles.desktopRowViewport} key={`desktop-row-${rowIndex}`}>
                <Marquee
                  className={styles.desktopMarquee}
                  speed={20}
                  pauseOnHover
                  direction={rowIndex === 1 ? 'right' : 'left'}
                >
                  <div
                    className={styles.desktopRow}
                    style={
                      {
                        '--ai-row-offset': DESKTOP_ROW_OFFSETS[rowIndex] || '0px',
                      } as React.CSSProperties
                    }
                  >
                    {row.map((item, itemIndex) => (
                      <AiForGoodCard
                        key={`${item.aiForGoodID || `${rowIndex}-${itemIndex}`}-${itemIndex}`}
                      item={item}
                      locale={locale}
                      site={site}
                      // Figma 中每一组跑马灯的首张卡片宽度比其他卡片少 1px，重复组也保持这个节奏。
                      isLeadDesktopCard={
                          rowIndex === 0
                            ? sourceFirstRow.length > 0 &&
                              itemIndex % sourceFirstRow.length === 0
                            : sourceSecondRow.length > 0 &&
                              itemIndex % sourceSecondRow.length === 0
                        }
                        animationDelay={rowIndex * 120 + (itemIndex % 6) * 60}
                      />
                    ))}
                  </div>
                </Marquee>
              </div>
            ) : null
          )}
        </div>
      ) : null}

      {list.length ? (
        <div className={styles.mobileRows}>
          {mobileRows.map((row, rowIndex) => (
            <div className={styles.mobileRowViewport} key={`mobile-row-${rowIndex}`}>
              <Marquee
                className={styles.mobileMarquee}
                speed={20}
                pauseOnHover
                pauseOnClick
                direction={rowIndex === 1 ? 'right' : 'left'}
              >
                <div className={styles.mobileRow}>
                  {row.map((item, itemIndex) => (
                    <AiForGoodCard
                      key={`${item.aiForGoodID || `mobile-${rowIndex}-${itemIndex}`}-${itemIndex}`}
                      item={item}
                      locale={locale}
                      site={site}
                      isMobile
                      animationDelay={itemIndex * 60}
                    />
                  ))}
                </div>
              </Marquee>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
